// web/src/types/advanced.ts - Attack path, priority, tickets, AI, exposure, integrations, API keys, monitored alerts, archives

import type { User } from './user';
import type { Project } from './project';
import type { Target } from './target';
import type { Scan } from './scan';
import type { ScanResult } from './finding';
import type { Client } from './client';

// ============================================================================
// ATTACK PATH VISUALIZATION & INTELLIGENT PRIORITIZATION
// ============================================================================

export interface AttackPathNode {
  id: string;
  label: string;
  type: 'host' | 'service' | 'vulnerability' | 'entry_point' | 'pivot_point' | 'target';
  ip?: string;
  hostname?: string;
  port?: number;
  service?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | 'info' | 'system';
  cvss?: number;
  cve?: string;
  risk_score: number;
  criticality?: 'critical' | 'high' | 'medium' | 'low' | 'unknown';
  is_external: boolean;
  is_pivot: boolean;
  result_id?: number;
  // MITRE ATT&CK mapping
  mitre_technique?: string;
  mitre_tactic?: string;
  mitre_url?: string;
  // D3.js visualization properties
  group: number;
  size: number;
  x?: number;
  y?: number;
  // Additional metadata
  open_ports?: number[];
  technologies?: string[];
  vuln_count?: number;
}

export interface AttackPathEdge {
  source: string;
  target: string;
  type: 'network' | 'exploitation' | 'lateral_movement' | 'privilege_escalation' | 'credential_access' | 'initial_access';
  label?: string;
  weight: number;
  exploitable: boolean;
  // MITRE ATT&CK mapping
  mitre_technique?: string;
  mitre_tactic?: string;
  // Visual properties for D3.js
  animated: boolean;
  dashed: boolean;
  color: string;
  thickness: number;
}

export interface AttackPathStep {
  step_number: number;
  from_node: string;
  to_node: string;
  action: string;
  technique?: string;
  technique_id?: string;
  tactic?: string;
  difficulty: 'easy' | 'medium' | 'hard';
  risk_score: number;
  cve?: string;
  exploit_available?: boolean;
  detection_risk?: 'low' | 'medium' | 'high';
}

export interface AttackPath {
  id: string;
  name: string;
  description: string;
  nodes: string[];
  total_risk: number;
  steps: AttackPathStep[];
  entry_point: string;
  final_target: string;
  is_critical: boolean;
  // Additional path metadata
  affected_assets: number;
  cves_in_path?: string[];
  tactics_used?: string[];
  techniques_used?: string[];
  estimated_time?: string;
  skill_level?: 'script_kiddie' | 'intermediate' | 'advanced' | 'nation_state';
  likelihood: number;
}

export interface AttackPathSummary {
  total_hosts: number;
  external_hosts: number;
  internal_hosts: number;
  critical_assets: number;
  pivot_points: number;
  critical_paths: number;
  high_risk_paths: number;
  average_path_length: number;
  max_risk_score: number;
  lateral_move_risk: 'low' | 'medium' | 'high' | 'critical';
}

export interface AttackPathResponse {
  scan_id: number;
  nodes: AttackPathNode[];
  edges: AttackPathEdge[];
  paths: AttackPath[];
  summary: AttackPathSummary;
  graphviz_dot?: string;
}

export interface PriorityScore {
  result_id: number;
  cve?: string;
  title: string;
  host: string;
  port: number;
  service: string;

  // Base scores
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info' | 'system';
  cvss: number;
  epss_score: number;
  epss_percentile: number;

  // Context factors
  asset_criticality: string;
  network_exposure: 'internet' | 'dmz' | 'internal' | 'unknown';
  data_sensitivity: string;
  environment: string;

  // Calculated scores (0-100)
  base_score: number;
  threat_score: number;
  context_score: number;
  exposure_score: number;

  // Final priority
  priority_score: number;
  priority_label: 'Critical' | 'High' | 'Medium' | 'Low';
  priority_rank: number;
  recommended_action: string;
  remediation_window: 'Immediate' | '7 days' | '30 days' | '90 days';
}

export interface PriorityConfig {
  cvss_weight: number;
  epss_weight: number;
  criticality_weight: number;
  exposure_weight: number;
}

export interface PriorityScoresResponse {
  scan_id: number;
  total: number;
  config: PriorityConfig;
  scores: PriorityScore[];
}

export interface PrioritySummary {
  scan_id: number;
  total: number;
  by_priority: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  by_severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  by_remediation_window: {
    immediate: number;
    seven_days: number;
    thirty_days: number;
    ninety_days: number;
  };
  config: PriorityConfig;
}

export const DEFAULT_PRIORITY_CONFIG: PriorityConfig = {
  cvss_weight: 0.25,
  epss_weight: 0.30,
  criticality_weight: 0.25,
  exposure_weight: 0.20,
};

// ============================================================================
// TICKET SYSTEM TYPES
// ============================================================================

export type TicketStatus = 'open' | 'in_progress' | 'pending_customer' | 'pending_admin' | 'resolved' | 'closed';
export type TicketPriority = 'critical' | 'high' | 'medium' | 'low';
export type TicketCategory = 'agent_limit' | 'scan_issue' | 'feature_request' | 'bug_report' | 'access_request' | 'billing' | 'other';
export type TicketSource = 'system' | 'user' | 'client' | 'admin';

export interface Ticket {
  id: number;
  ticket_uid: string;
  client_id?: number;
  client?: Client;
  project_id?: number;
  project?: Project;
  created_by_id: number;
  created_by?: User;
  assigned_to_id?: number;
  assigned_to?: User;
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  source: TicketSource;
  ref_type?: string;
  ref_id?: string;
  acknowledged: boolean;
  acknowledged_at?: string;
  acknowledged_by_id?: number;
  acknowledged_by?: User;
  resolved_at?: string;
  resolved_by_id?: number;
  resolved_by?: User;
  closed_at?: string;
  internal_notes?: string;
  due_date?: string;
  replies?: TicketReply[];
  created_at: string;
  updated_at: string;
}

export interface TicketReply {
  id: number;
  ticket_id: number;
  user_id: number;
  user?: User;
  message: string;
  is_internal: boolean;
  is_system_reply?: boolean;
  system_action?: string;
  created_at: string;
  updated_at: string;
}

export interface SystemNotification {
  id: number;
  user_id?: number;
  client_id?: number;
  type: 'info' | 'warning' | 'error' | 'success' | 'ticket' | 'scan_complete' | 'agent_limit' | 'security_alert';
  title: string;
  message: string;
  ref_type?: string;
  ref_id?: number;
  is_read: boolean;
  is_dismissed: boolean;
  created_at: string;
  expires_at?: string;
}

export interface UserNotificationPreferences {
  id: number;
  user_id: number;
  email_on_ticket: boolean;
  email_on_ticket_reply: boolean;
  email_on_scan_complete: boolean;
  email_on_high_severity: boolean;
  email_on_agent_limit: boolean;
  email_digest_frequency: 'none' | 'daily' | 'weekly';
  in_app_notifications: boolean;
  created_at: string;
  updated_at: string;
}

export interface TicketFilters {
  status?: TicketStatus[];
  priority?: TicketPriority[];
  category?: TicketCategory[];
  source?: TicketSource[];
  client_id?: number;
  assigned_to_id?: number;
  search?: string;
}

// ============================================================================
// AI SUMMARY INTERFACES
// ============================================================================

export interface AIScanSummary {
  id: number;
  scan_id: number;
  summary_en: string;
  summary_de: string;
  summary_fr: string;
  summary_it: string;
  key_findings: string;
  recommendations: string;
  risk_assessment: string;
  model_used: string;
  tokens_used: number;
  generated_at: string;
  created_at: string;
  updated_at: string;
}

export interface AIScanComparison {
  id: number;
  scan_id: number;
  previous_scan_id?: number;
  comparison_en: string;
  comparison_de: string;
  comparison_fr: string;
  comparison_it: string;
  changes_summary: string;
  trend_analysis: string;
  model_used: string;
  tokens_used: number;
  generated_at: string;
  created_at: string;
  updated_at: string;
}

export interface AIStatus {
  enabled: boolean;
  system_enabled: boolean;
  has_key: boolean;
  client_ai_enabled: boolean;
  client_disabled_reason?: string;
  model: string;
  base_url: string;
  log_requests: boolean;
}

// ============================================================================
// EXPOSURE MONITORING TYPES
// ============================================================================

export interface ExposureSnapshot {
  id: number;
  project_id: number;
  name: string;
  description?: string;
  source_scan_id?: number;
  is_baseline: boolean;
  total_findings: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  info_count: number;
  unique_host_count: number;
  unique_port_count: number;
  fingerprints?: string;
  created_by_id: number;
  created_at: string;
  // Relations
  project?: Project;
  source_scan?: Scan;
  created_by?: User;
}

// All possible exposure event types
export type ExposureEventType =
  // Finding events
  | 'new_finding'
  | 'resolved_finding'
  | 'severity_change'
  | 'new_critical_finding'
  | 'new_kev_finding'
  | 'finding_resolved'
  | 'finding_regressed'
  // Host/Network events
  | 'new_host'
  | 'lost_host'
  | 'new_service'
  | 'new_subdomain'
  | 'subdomain_gone'
  | 'new_open_port'
  | 'port_closed'
  | 'service_changed'
  | 'service_upgrade'
  | 'service_downgrade'
  // Web events
  | 'new_vhost'
  | 'new_login_page'
  | 'new_admin_panel'
  // Certificate events
  | 'new_tls_cert'
  | 'cert_expiring_soon'
  | 'cert_expiring_critical'
  | 'cert_expired'
  | 'cert_issuer_changed';

export interface ExposureEvent {
  id: number;
  project_id: number;
  scan_id: number;
  event_type: ExposureEventType;
  event_category?: string; // subdomain, port, service, web, tls, finding
  fingerprint?: string;
  result_id?: number;
  host?: string;
  port?: number;
  service?: string;
  title?: string;
  severity?: string;
  cve_id?: string;
  previous_value?: string;
  new_value?: string;
  compared_to_snapshot_id?: number;
  compared_to_scan_id?: number;
  notification_sent: boolean;
  notified_at?: string;
  notified_contacts?: string;
  acknowledged_at?: string;
  acknowledged_by_id?: number;
  created_at: string;
  // Certificate-related fields
  subject?: string;
  subject_type?: string;
  cert_expiry?: string;
  days_until_expiry?: number;
  // Relations
  project?: Project;
  scan?: Scan;
  result?: ScanResult;
  acknowledged_by?: User;
  // Source table identifier (for acknowledgment)
  source?: 'standard' | 'enhanced';
}

// Exposure Notification Rule (Policy) Type
export interface ExposureNotificationRule {
  id: number;
  client_id: number;
  project_id?: number;
  name: string;
  description: string;
  event_types: string; // JSON array of event types
  event_categories: string; // JSON array of categories
  min_severity: string;
  only_internet_exposed: boolean;
  only_production: boolean;
  asset_patterns?: string;
  exclude_patterns?: string;
  notify_immediately: boolean;
  include_in_digest: boolean;
  digest_frequency: string;
  notify_via_email: boolean;
  notify_contacts?: string;
  webhook_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Relations
  client?: Client;
  project?: Project;
}

// ============================================================================
// INTEGRATION WEBHOOK TYPES
// ============================================================================

export type IntegrationType = 'jira' | 'github' | 'gitlab' | 'servicenow' | 'webhook';
export type AuthType = 'basic' | 'token' | 'oauth' | 'api_key';

export interface IntegrationWebhook {
  id: number;
  name: string;
  integration_type: IntegrationType;
  project_id?: number;
  client_id?: number;
  base_url: string;
  auth_type?: AuthType;
  auth_config?: string; // Usually "[ENCRYPTED]" in responses
  jira_project_key?: string;
  jira_issue_type?: string;
  github_owner?: string;
  github_repo?: string;
  webhook_headers?: string;
  webhook_payload?: string;
  trigger_events?: string;
  min_severity: string;
  is_active: boolean;
  last_test_at?: string;
  last_test_ok: boolean;
  last_error_msg?: string;
  total_sent: number;
  total_failed: number;
  last_sent_at?: string;
  created_by_id: number;
  created_at: string;
  updated_at: string;
  // Relations
  project?: Project;
  client?: Client;
  created_by?: User;
}

export interface IntegrationWebhookLog {
  id: number;
  webhook_id: number;
  event_type?: string;
  request_method?: string;
  request_url?: string;
  request_body?: string;
  response_code?: number;
  response_body?: string;
  success: boolean;
  error_msg?: string;
  duration_ms: number;
  finding_ticket_id?: number;
  result_id?: number;
  scan_id?: number;
  created_at: string;
}

export interface IntegrationWebhookCreateRequest {
  name: string;
  integration_type: IntegrationType;
  project_id?: number;
  client_id?: number;
  base_url: string;
  auth_type?: AuthType;
  auth_config?: Record<string, unknown>;
  jira_project_key?: string;
  jira_issue_type?: string;
  github_owner?: string;
  github_repo?: string;
  webhook_headers?: Record<string, string>;
  webhook_payload?: string;
  trigger_events?: string[];
  min_severity?: string;
}

// ============================================================================
// CLIENT API KEY INTERFACES
// ============================================================================

// Permission types for client API keys
// Keys are strictly scoped to the client - they can ONLY access the client's own resources
export type ClientAPIKeyPermission =
  | 'read'            // Read access to projects, scans, results, tickets
  | 'write'           // Create/update tickets, add comments
  | 'scan'            // Trigger scans within client's projects
  | 'export'          // Export reports for client's scans
  | 'manage_contacts' // Manage client contacts
  | 'manage_agents';  // Manage agents assigned to client

export interface ClientAPIKey {
  id: number;
  client_id: number;
  name: string;
  key_prefix: string;  // First 12 chars for identification (e.g., "vscn_abc123")
  // key_hash is never exposed in responses

  // Permissions - controls what operations are allowed within the client's scope
  permissions: string; // JSON array of ClientAPIKeyPermission

  // Rate limiting
  rate_limit_per_minute: number;
  rate_limit_per_hour: number;

  // Usage tracking
  last_used_at?: string;
  last_used_ip?: string;
  total_requests: number;

  // Validity
  expires_at?: string;
  is_active: boolean;
  revoked_at?: string;
  revoked_by?: number;
  revoke_reason?: string;

  // Ownership
  created_by_id: number;
  created_at: string;
  updated_at: string;

  // Relations
  client?: Client;
  created_by?: User;
  revoked_by_user?: User;
}

export interface ClientAPIKeyLog {
  id: number;
  key_id: number;
  method?: string;
  path?: string;
  ip_address?: string;
  user_agent?: string;
  status_code?: number;
  success: boolean;
  error_msg?: string;
  duration_ms: number;
  created_at: string;
}

export interface ClientAPIKeyStats {
  total_requests: number;
  successful_calls: number;
  failed_calls: number;
  avg_duration_ms: number;
  last_24_hours: number;
  last_hour: number;
}

export interface ClientAPIKeyCreateRequest {
  client_id: number;
  name: string;
  permissions?: ClientAPIKeyPermission[];
  rate_limit_per_minute?: number;
  rate_limit_per_hour?: number;
  expires_at?: string;
}

export interface ClientAPIKeyCreateResponse {
  message: string;
  api_key: ClientAPIKey;
  key: string;  // Full key - only returned once on creation!
  warning: string;
}

// ============================================================================
// MONITORED ALERTS INTERFACES (Proactive SSL Certificate Expiry Monitoring)
// ============================================================================

export interface MonitoredAlert {
  id: number;
  project_id: number;
  target_id?: number;
  client_id?: number;

  // Monitor type
  monitor_type: 'ssl_certificate' | 'domain_expiry';
  subject: string;       // e.g., "example.com:443"
  subject_type: string;  // e.g., "host:port"

  // Certificate/expiry specific data
  expiry_date: string;
  issuer?: string;
  subject_cn?: string;
  fingerprint?: string;

  // Alert thresholds
  alert_30_days: boolean;
  alert_14_days: boolean;
  alert_7_days: boolean;
  alert_1_day: boolean;
  alert_expired: boolean;

  // Alert tracking (timestamps of when thresholds were alerted)
  alerted_30_days_at?: string;
  alerted_14_days_at?: string;
  alerted_7_days_at?: string;
  alerted_1_day_at?: string;
  alerted_expired_at?: string;

  // Status
  is_active: boolean;
  is_paused: boolean;
  pause_reason?: string;

  // Source tracking
  source_scan_id?: number;
  last_verified_at?: string;
  last_verified_scan_id?: number;

  // Metadata
  created_at: string;
  updated_at: string;
  created_by_id?: number;

  // Relations (if preloaded)
  project?: Project;
  target?: Target;
}

export interface MonitoredAlertStats {
  total: number;
  active: number;
  paused: number;
  expiring_7_days: number;
  expiring_14_days: number;
  expiring_30_days: number;
  expired: number;
  ssl_certificates: number;
  domain_expiry: number;
}

export interface MonitoredAlertUpdateRequest {
  alert_30_days?: boolean;
  alert_14_days?: boolean;
  alert_7_days?: boolean;
  alert_1_day?: boolean;
  alert_expired?: boolean;
  is_active?: boolean;
  is_paused?: boolean;
  pause_reason?: string;
}

export interface ProactiveAlertStatus {
  enabled: boolean;
  check_hour: number;
  check_minute: number;
  last_check?: string;
  total_alerts: number;
  active_alerts: number;
  expiring_7_days: number;
}

// ============================================================================
// SCAN ARCHIVES
// ============================================================================

export interface ScanArchive {
  id: number;
  original_scan_id: number;
  project_id: number;
  project_name: string;
  scan_name: string;
  description: string;
  status: string;
  started_at?: string;
  completed_at?: string;
  duration_seconds: number;
  schedule_id?: number;
  execution_number?: number;
  is_agent_scan: boolean;
  agent_id: string;
  target_count: number;
  targets_json: string;
  scan_config_json: string;
  total_findings: number;
  total_results: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  info_count: number;
  unique_cves: number;
  unique_hosts: number;
  ports_scanned: number;
  risk_grade: string;
  risk_score: number;
  nuclei_engine_type: string;
  nuclei_templates_executed: number;
  nuclei_results_found: number;
  nuclei_total_execution_ms: number;
  engine_timings_json: string;
  report_size_bytes: number;
  source_data_exists: boolean;
  source_purged_at?: string;
  purged_by?: string;
  archived_at: string;
  created_at: string;
  updated_at: string;
  findings?: ScanArchiveFinding[];
}

export interface ScanArchiveFinding {
  id: number;
  scan_archive_id: number;
  original_result_id: number;
  host: string;
  ip: string;
  port: number;
  protocol: string;
  service: string;
  vuln_type: string;
  severity: string;
  title: string;
  cve_id: string;
  cvss: number;
  epss_score: number;
  kev_listed: boolean;
  exploit_confirmed: boolean;
  remediation_status: string;
  is_false_positive: boolean;
  is_suppressed: boolean;
  first_seen_at?: string;
  created_at: string;
}

export interface ArchiveStats {
  total_archives: number;
  total_findings: number;
  avg_findings_per_scan: number;
  with_source_data: number;
  without_source_data: number;
  total_report_bytes: number;
  by_project: { project_id: number; project_name: string; archive_count: number; finding_count: number }[];
  by_severity: Record<string, number>;
}
